import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { renderFields } from '@highnoteplatform/card-viewer'
import { Images } from '@/theme/Images'
import './card-viewer.css'
import { Copy } from 'lucide-react'
import Spinner from '@/assets/icons/spinner'

export const CardViewerComponent = ({ clientToken, paymentCardId }) => {
  const containerRef = useRef(null)

  useEffect(() => {
    let unmount = null
    const render = async () => {
      try {
        // Clear previous contents to apply new styles cleanly (scoped to this component only)
        // This code is necessary because of how the Highnote Card Viewer SDK works internally.
        const scope = containerRef.current
        if (scope) {
          [('#cardNumber', '#expirationDate', '#cvv')].map(sel => {
            const el = scope.querySelector(sel)
            if (el) el.innerHTML = ''
          })
          scope.querySelector('#expirationDateAndCVV')?.classList.add('hidden')
          scope.querySelector('#cardNumberWrapper')?.classList.add('hidden')
          scope.querySelector('#spinner')?.classList.remove('hidden')
        }
        const res = await renderFields({
          clientToken,
          paymentCardId,
          enableClipboard: true,
          environment: import.meta.env.VITE_SENTRY_ENVIRONMENT === 'production' ? 'live' : 'test',
          maskedOnLoad: false,
          onError: error => {
            console.error('Error rendering card viewer:', error)
          },
          onReady: () => {
            const scope = containerRef.current
            scope?.querySelector('#cardNumber')?.classList.remove('hidden')
            scope?.querySelector('#expirationDate')?.classList.remove('hidden')
            scope?.querySelector('#cvv')?.classList.remove('hidden')
            scope?.querySelector('#expirationDateAndCVV')?.classList.remove('hidden')
            scope?.querySelector('#cardNumberWrapper')?.classList.remove('hidden')
            scope?.querySelector('#spinner')?.classList.add('hidden')
          },
          elements: {
            cardNumber: {
              selector: '#cardNumber',
              styles: { fontWeight: 500, fontSize: '18px', color: '#232429', cursor: 'pointer' },
            },
            cvv: { selector: '#cvv', styles: { fontWeight: 500, fontSize: '18px', color: '#232429' } },
            expirationDate: {
              selector: '#expirationDate',
              styles: { fontWeight: 500, fontSize: '18px', color: '#232429' },
            },
          },
        })
        unmount = res.unmount
      } catch (err) {
        console.error('Error initializing card viewer:', err)
      }
    }
    render()
    return () => {
      unmount?.()
    }
  }, [clientToken, paymentCardId])

  return (
    <div
      id="card-viewer"
      ref={containerRef}
      className={`w-full h-auto relative mb-5`}
      data-testid="credit-card-mockup-container"
    >
      <img className="w-full h-auto block" src={Images.creditCardMockupNew} alt="credit card mockup" />
      {/* Card Number */}
      <div id="cardNumberWrapper" className="flex h-5 font-medium mt-5 mb-4 hidden text-neutral-900 overflow-hidden">
        <div id="cardNumber" className="w-[185px] cursor-pointer text-lg text-neutral-900" />
        <Copy
          width={20}
          height={20}
          className="cursor-pointer rotate-90 stroke-neutral-400"
          onClick={() => {
            const iframe = document.getElementById('cardNumber')?.querySelector('iframe')

            if (!iframe) {
              console.error('No iframe found inside #cardNumber')
              return
            }

            try {
              // Access iframe document
              const iframeDoc = iframe.contentDocument || iframe.contentWindow.document

              // Get the #field inside the iframe’s body
              const fieldValue = iframeDoc.querySelector('#field')?.textContent?.trim()

              if (!fieldValue) {
                console.error("No #field found inside iframe or it's empty")
                return
              }

              // Copy to clipboard
              navigator.clipboard
                .writeText(fieldValue)
                .then(() => console.log('Copied:', fieldValue))
                .catch(err => console.error('Clipboard error:', err))
            } catch (e) {
              console.error('Iframe is cross-origin, cannot read content.', e)
            }
          }}
        />
      </div>
      <custom id="expirationDateAndCVV" className="flex flex-row h-5 gap-8 ml-1 overflow-hidden">
        <div className="flex flex-row items-center">
          <p className="text-neutral-700 font-normal">Exp</p>
          <div id="expirationDate" className="h-5 w-[50px] text-neutral-900 font-medium text-lg" />
        </div>

        <div className="flex flex-row items-center h-5">
          <p className="text-neutral-700 font-normal">CVV</p>
          <div id="cvv" className="h-5 w-[30px] text-neutral-900 font-medium text-lg" />
        </div>
      </custom>
      <div id="spinner" className="mt-5">
        <Spinner size="20" stroke="#B91D7B" />
      </div>
    </div>
  )
}

CardViewerComponent.propTypes = {
  clientToken: PropTypes.string.isRequired,
  paymentCardId: PropTypes.string.isRequired,
  cardHolderName: PropTypes.string,
}
